import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Chip,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Container,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  AccountBalance as BankIcon,
  Flag as FlagIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import paymentService, {
  Transaction,
  WithdrawalRequest,
} from '../services/api/paymentService';

interface RevenueMetrics {
  today: number;
  week: number;
  month: number;
}

interface PaymentStats {
  totalEscrow: number;
  releasedFunds: number;
  platformEarnings: number;
  pendingWithdrawals: number;
}

export default function PaymentsPage() {
  const [activeTab, setActiveTab] = useState(0);

  // ── Transaction History state ────────────────────────────────────────────────
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<PaymentStats>({
    totalEscrow: 0,
    releasedFunds: 0,
    platformEarnings: 0,
    pendingWithdrawals: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  // ── Withdrawals state ────────────────────────────────────────────────────────
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [withdrawalStatusFilter, setWithdrawalStatusFilter] = useState<string>('pending');
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [withdrawalsError, setWithdrawalsError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null); // stores id of row being actioned

  // Approve confirmation dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState<WithdrawalRequest | null>(null);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<WithdrawalRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Snackbar feedback
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ── Load transaction history ─────────────────────────────────────────────────
  useEffect(() => {
    loadTransactions();
  }, [paginationModel, searchQuery, statusFilter, typeFilter]);

  // ── Load withdrawals when tab selected or filter changes ─────────────────────
  useEffect(() => {
    if (activeTab === 1) loadWithdrawals();
  }, [activeTab, withdrawalStatusFilter]);

  const calculateStats = (txList: Transaction[]) => {
    const pendingWithdrawals = txList.filter(
      (t) => t.type === 'withdrawal' && t.status === 'pending'
    ).length;
    const escrowTotal = txList
      .filter((t) => t.type === 'payment')
      .reduce((sum, t) => sum + t.amount, 0);
    const releasedFundsTotal = txList
      .filter((t) => t.type === 'withdrawal' && t.status === 'completed')
      .reduce((sum, t) => sum + t.amount, 0);
    const platformEarningsTotal = txList
      .filter((t) => t.type === 'commission')
      .reduce((sum, t) => sum + t.amount, 0);
    return { totalEscrow: escrowTotal, releasedFunds: releasedFundsTotal, platformEarnings: platformEarningsTotal, pendingWithdrawals };
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const [transactionsData] = await Promise.all([
        paymentService.getTransactions(
          statusFilter || undefined,
          typeFilter || undefined,
          1000
        ),
      ]);

      let filteredTx = transactionsData.transactions || [];
      setAllTransactions(filteredTx);

      if (searchQuery) {
        filteredTx = filteredTx.filter(
          (t) =>
            t.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.user?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.phone?.includes(searchQuery)
        );
      }

      const paymentStats = calculateStats(filteredTx);
      setStats(paymentStats);
      setTotalTransactions(filteredTx.length);

      const start = paginationModel.page * paginationModel.pageSize;
      setTransactions(filteredTx.slice(start, start + paginationModel.pageSize));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    const rows = [
      'ID,Type,Amount,Reference,Status,Date',
      ...allTransactions.map(t =>
        `${t.id},${t.type},${t.amount},${t.reference},${t.status},${t.created_at}`
      ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const loadWithdrawals = async () => {
    try {
      setWithdrawalsLoading(true);
      setWithdrawalsError(null);
      const data = await paymentService.getWithdrawals(withdrawalStatusFilter);
      setWithdrawals(data.withdrawals || []);
    } catch (err: any) {
      setWithdrawalsError(err.response?.data?.message || 'Failed to load withdrawals');
    } finally {
      setWithdrawalsLoading(false);
    }
  };

  // ── Withdrawal approve / reject actions ──────────────────────────────────────
  const openApproveDialog = (wr: WithdrawalRequest) => {
    setApproveTarget(wr);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    setApproveDialogOpen(false);
    setActionLoading(approveTarget.id);
    try {
      await paymentService.approveWithdrawal(approveTarget.id);
      setSnackbar({ open: true, message: `✅ ₦${approveTarget.amount.toLocaleString()} withdrawal approved`, severity: 'success' });
      loadWithdrawals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to approve', severity: 'error' });
    } finally {
      setActionLoading(null);
      setApproveTarget(null);
    }
  };

  const openRejectDialog = (wr: WithdrawalRequest) => {
    setRejectTarget(wr);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionLoading(rejectTarget.id);
    setRejectDialogOpen(false);
    try {
      await paymentService.rejectWithdrawal(rejectTarget.id, rejectReason);
      setSnackbar({ open: true, message: `❌ Withdrawal rejected — ₦${rejectTarget.amount.toLocaleString()} refunded to wallet`, severity: 'success' });
      loadWithdrawals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to reject', severity: 'error' });
    } finally {
      setActionLoading(null);
      setRejectTarget(null);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
    };
    return colors[status] || 'default';
  };

  const getTypeColor = (type: string): 'primary' | 'warning' | 'info' | 'success' | 'default' => {
    const colors: Record<string, 'primary' | 'warning' | 'info' | 'success' | 'default'> = {
      payment: 'primary',
      refund: 'warning',
      withdrawal: 'info',
      commission: 'success',
    };
    return colors[type] || 'default';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      payment: 'Payment',
      refund: 'Refund',
      withdrawal: 'Withdrawal',
      commission: 'Commission',
    };
    return labels[type] || type;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount);

  // ── DataTable columns ────────────────────────────────────────────────────────
  const columns: GridColDef[] = [
    {
      field: 'reference',
      headerName: 'Transaction ID',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'user',
      headerName: 'User',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.phone && (
            <Typography variant="caption" color="text.secondary">
              {params.row.phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right' as const,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip label={getTypeLabel(params.value)} color={getTypeColor(params.value)} variant="filled" size="small" />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value.charAt(0).toUpperCase() + params.value.slice(1)}
          color={getStatusColor(params.value)}
          variant="filled"
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">{new Date(params.value).toLocaleDateString()}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button size="small" onClick={() => { setSelectedTransaction(params.row); setOpenDetailsDialog(true); }}>
          Details
        </Button>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon sx={{ fontSize: 32 }} />
          Payments & Transactions
        </Typography>
      </Box>

      {/* Metric Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Escrow" value={formatCurrency(stats.totalEscrow)} subtitle="Funds in escrow" icon={<BankIcon />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Released Funds" value={formatCurrency(stats.releasedFunds)} subtitle="Withdrawn funds" icon={<TrendingIcon />} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Platform Earnings" value={formatCurrency(stats.platformEarnings)} subtitle="Commission received" icon={<MoneyIcon />} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Pending Withdrawals" value={stats.pendingWithdrawals} subtitle="Awaiting processing" icon={<FlagIcon />} color="warning" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Transaction History" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Withdrawal Requests
                {stats.pendingWithdrawals > 0 && (
                  <Chip label={stats.pendingWithdrawals} size="small" color="warning" />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* ── TAB 0: Transaction History ───────────────────────────────────────── */}
      {activeTab === 0 && (
        <>
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {/* Filters */}
          <Card sx={{ mb: 3, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Filters</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by reference, user, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={typeFilter} label="Type" onChange={(e) => { setTypeFilter(e.target.value); setPaginationModel({ ...paginationModel, page: 0 }); }}>
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="payment">Payment</MenuItem>
                    <MenuItem value="refund">Refund</MenuItem>
                    <MenuItem value="withdrawal">Withdrawal</MenuItem>
                    <MenuItem value="commission">Commission</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPaginationModel({ ...paginationModel, page: 0 }); }}>
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button fullWidth variant="outlined" onClick={() => { setSearchQuery(''); setStatusFilter(''); setTypeFilter(''); setPaginationModel({ ...paginationModel, page: 0 }); }}>
                  Clear Filters
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button fullWidth variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV}>Export CSV</Button>
              </Grid>
            </Grid>
          </Card>

          <DataTable
            title="Transaction History"
            columns={columns}
            rows={transactions}
            rowCount={totalTransactions}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            loading={loading}
            error={error}
            searchPlaceholder="Search transactions..."
          />
        </>
      )}

      {/* ── TAB 1: Withdrawals ───────────────────────────────────────────────── */}
      {activeTab === 1 && (
        <>
          {withdrawalsError && <Alert severity="error" sx={{ mb: 3 }}>{withdrawalsError}</Alert>}

          {/* Withdrawal filters + refresh */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={withdrawalStatusFilter}
                label="Status"
                onChange={(e) => setWithdrawalStatusFilter(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed / Rejected</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={loadWithdrawals} disabled={withdrawalsLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {withdrawalsLoading && <CircularProgress size={20} />}
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Artisan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Bank / Account</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Requested</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Fee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Net Payout</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  {withdrawalStatusFilter === 'pending' && (
                    <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawals.length === 0 && !withdrawalsLoading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No {withdrawalStatusFilter === 'all' ? '' : withdrawalStatusFilter} withdrawal requests
                    </TableCell>
                  </TableRow>
                )}
                {withdrawals.map((wr) => (
                  <TableRow key={wr.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{wr.user_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{wr.user_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{wr.bank_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {wr.account_number} · {wr.account_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(wr.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {wr.is_first_daily_withdrawal ? 'Free' : formatCurrency(wr.convenience_fee)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatCurrency(wr.net_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={wr.status.charAt(0).toUpperCase() + wr.status.slice(1)}
                        color={getStatusColor(wr.status)}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(wr.created_at).toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </Typography>
                    </TableCell>
                    {withdrawalStatusFilter === 'pending' && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Approve withdrawal">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => openApproveDialog(wr)}
                                disabled={actionLoading === wr.id}
                              >
                                {actionLoading === wr.id
                                  ? <CircularProgress size={18} />
                                  : <ApproveIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Reject withdrawal">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openRejectDialog(wr)}
                                disabled={actionLoading === wr.id}
                              >
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* ── Transaction Details Dialog ──────────────────────────────────────── */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon />
          Transaction Details
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedTransaction && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Transaction ID</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace' }}>{selectedTransaction.reference}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>User</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedTransaction.user}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Phone</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{selectedTransaction.phone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Amount</Typography>
                <Typography variant="h6" sx={{ mt: 0.5, color: 'success.main' }}>{formatCurrency(selectedTransaction.amount)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Type</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip label={getTypeLabel(selectedTransaction.type)} color={getTypeColor(selectedTransaction.type)} variant="filled" size="small" />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Status</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={selectedTransaction.status.charAt(0).toUpperCase() + selectedTransaction.status.slice(1)}
                    color={getStatusColor(selectedTransaction.status)}
                    variant="filled"
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Date</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {new Date(selectedTransaction.created_at).toLocaleDateString()} at{' '}
                  {new Date(selectedTransaction.created_at).toLocaleTimeString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Approve Confirmation Dialog ─────────────────────────────────────── */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApproveIcon />
          Confirm Approval
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {approveTarget && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You are about to approve a withdrawal of{' '}
                <strong style={{ fontSize: '1.1em' }}>{formatCurrency(approveTarget.amount)}</strong>{' '}
                for <strong>{approveTarget.user_name}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Net payout to artisan: <strong>{formatCurrency(approveTarget.net_amount)}</strong>
                {!approveTarget.is_first_daily_withdrawal && (
                  <> (after {formatCurrency(approveTarget.convenience_fee)} convenience fee)</>
                )}
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                This will trigger an immediate bank transfer. This action cannot be undone.
              </Alert>
            </>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={handleApproveConfirm}>
            Approve & Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
          Reject Withdrawal
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {rejectTarget && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Reject <strong>{rejectTarget.user_name}</strong>'s withdrawal of{' '}
              <strong>{formatCurrency(rejectTarget.amount)}</strong>?
              The full amount will be refunded to their wallet immediately.
            </Typography>
          )}
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            placeholder="e.g. Invalid bank account details"
            multiline
            rows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject & Refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar feedback ───────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
